import React, { useContext, useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { formatLocation, normalizeHourString, validateHours } from '@utils/utils';
import { ApiContext } from '@api/api';
import {
  CompanyLocationsListPreviewOutDTO,
  ExceptionTimeIntervalInDTO,
  LocationSelectModeEnum,
  OpenHourExceptionTypeEnum,
} from '@api/ringil-api.v2';
import { MultiSelect } from 'primereact/multiselect';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  stringYMDFromDate,
  stringYMDFromDayjs,
  shortDateFormat,
  dayJsInterval,
  dayjsFromLocalDateString,
  dateFromDayjs
} from '@utils/dateWithTz';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import RadioButtons from '@components/obsolete/RadioButtons.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import { useTranslation } from 'react-i18next';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import InputHourManual from '@components/ringil3/Input/InputHour/InputHourManual.tsx';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onAdded: () => void;
  // When set, the sidebar opens scoped to this location (SELECTED mode, that location enabled). Omitted = unchanged ALL_LOCATIONS default.
  preselectLocationId?: string;
}

enum LocationSelectionMode {
  ALL_LOCATIONS = 'ALL_LOCATIONS',
  SELECTED_LOCATIONS = 'SELECTED_LOCATIONS',
}

enum OpenHoursExceptionType {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

interface FormData {
  dateRanges: Date[][];
  exceptionName: string;
  locationSelectMode: LocationSelectModeEnum;
  openHourExceptionType: OpenHourExceptionTypeEnum;
  openingHours: ExceptionTimeIntervalInDTO[];
  timeZone: string;
  locationsAndRamps: LocationAndRamps[];
  isOnlyForExternalPartners: boolean;
}

interface LocationAndRamps {
  locationName: string;
  locationId: string;
  streetNr: string;
  city: string;
  postalCode: string;
  country: string;
  enabled: boolean;
  ramps: string[];
}

const initialValues: FormData = {
  dateRanges: [[new Date()]],
  exceptionName: '',
  locationSelectMode: LocationSelectModeEnum.ALL_LOCATIONS,
  openHourExceptionType: OpenHourExceptionTypeEnum.CLOSED,
  openingHours: [],
  timeZone: 'Europe/Prague',
  locationsAndRamps: [],
  isOnlyForExternalPartners: false,
};

const expandRangesToDays = (dateRanges: Date[][]) =>
  (dateRanges || [])
    .filter(range => range?.[0])
    .flatMap(range =>
      dayJsInterval(
        dayjsFromLocalDateString(stringYMDFromDate(range[0])),
        range[1] ? dayjsFromLocalDateString(stringYMDFromDate(range[1])) : undefined,
      ),
    );

const NewExceptionSidebar: React.FC<ComponentProps> = ({ visible, onHide, onAdded, preselectLocationId }) => {
  const { tr } = useTranslationLgs();
  const { getCompanyLocations, createCompanyLocationException } = useContext(ApiContext);
  const [locations, setLocations] = useState<CompanyLocationsListPreviewOutDTO[]>([]);
  const preselectRef = useRef<string | undefined>(undefined);

  const [loading, setLoading] = useState<boolean>(false);

  const fromToScheme = yup.object().shape({
    since: yup.string().test({
      test: validateHours,
      name: 'time-ok',
      message: tr('validation.invalid', 'Value is invalid'),
      exclusive: false,
    }),
    till: yup.string().test({
      test: validateHours,
      name: 'time-ok',
      message: tr('validation.invalid', 'Value is invalid'),
      exclusive: false,
    }),
  });

  const validationSchema = yup.object().shape({
    exceptionName: yup.string().required(tr('validation.required', 'Value is required')),
    dateRanges: yup
      .array()
      .test('has-start', tr('validation.required', 'Value is required'), (ranges: Date[][]) =>
        (ranges || []).some(r => !!r?.[0]),
      )
      .test(
        'no-overlap',
        tr('NewExceptionSidebar.datesOverlap', 'Dates or intervals overlap — each day can be used only once'),
        (ranges: Date[][]) => {
          const ymds = expandRangesToDays(ranges).map(d => stringYMDFromDayjs(d));
          return new Set(ymds).size === ymds.length;
        },
      ),
    openingHours: yup
      .array()
      .of(fromToScheme)
      .when('openHourExceptionType', {
        is: OpenHourExceptionTypeEnum.OPEN,
        then: yup.array().of(fromToScheme).min(1, tr('NewExceptionSidebar.atLeastOneIntervalShouldBeCreated', 'At least one interval should be created')),
        otherwise: yup.array(),
      }),
  });

  const loadLocations = (preselectId?: string) => {
    getCompanyLocations(null, null, d => {
      const locAndRamps = d.map(item => {
        return {
          city: item.city,
          country: item.country,
          enabled: preselectId ? item.locationId === preselectId : false,
          locationName: item.name,
          locationId: item.locationId,
          ramps: [],
          postalCode: item.postalCode,
          streetNr: item.streetNr,
        } as LocationAndRamps;
      });
      setLocations(d);
      formik.setFieldValue('locationsAndRamps', locAndRamps);
    });
  };

  const createException = (values: FormData) => {
    setLoading(true);
    const days = expandRangesToDays(values.dateRanges);
    createCompanyLocationException(
      {
        exceptionName: values.exceptionName,
        dates: days.map(d => dateFromDayjs(d).toISOString()),
        locationsAndRamps: values.locationsAndRamps,
        openHourExceptionType: values.openHourExceptionType,
        locationSelectMode: values.locationSelectMode,
        openingHours: values.openingHours.map(oh => ({ since: normalizeHourString(oh.since), till: normalizeHourString(oh.till) })),
        timeZone: values.timeZone,
        isOnlyForExternalPartners: values.isOnlyForExternalPartners,
        localDates: days.map(d => stringYMDFromDayjs(d)),
      },
      () => {
        onAdded();
        setLoading(false);
      },
    );
  };

  const formik = useFormik<FormData>({
    initialValues,
    onSubmit: values => createException(values),
    validationSchema,
  });

  const addDate = () => {
    formik.setFieldValue('dateRanges', [...formik.values.dateRanges, [new Date()]]);
  };

  const deleteDate = (index: number) => {
    formik.setFieldValue('dateRanges', [...formik.values.dateRanges.filter((_, i) => i !== index)]);
  };

  const addOpeningTime = () => {
    formik.setFieldValue('openingHours', [...formik.values.openingHours, { since: '', till: '' }]);
  };
  const removeOpeningTime = (i: number) => {
    formik.setFieldValue('openingHours', [...formik.values.openingHours.filter((_, index) => index !== i)]);
  };

  useEffect(() => {
    formik.resetForm();
    if (visible && preselectLocationId) {
      preselectRef.current = preselectLocationId;
      formik.setFieldValue('locationSelectMode', LocationSelectModeEnum.SELECTED_LOCATIONS);
    }
  }, [visible]);

  useEffect(() => {
    if (formik.values.openHourExceptionType === OpenHourExceptionTypeEnum.CLOSED) {
      formik.setFieldValue('openingHours', []);
    }
  }, [formik.values.openHourExceptionType]);

  useEffect(() => {
    if (formik.values.locationSelectMode === LocationSelectModeEnum.SELECTED_LOCATIONS) {
      loadLocations(preselectRef.current);
      preselectRef.current = undefined;
    } else {
      formik.setFieldValue('locationsAndRamps', []);
    }
  }, [formik.values.locationSelectMode]);

  const { i18n } = useTranslation();

  return (
      <RingilSidebar
        visible={visible}
        onBack={onHide}
        onContinue={() => {
          formik.handleSubmit();
        }}
        heading={tr(`NewExceptionSidebar.openingTimeException`, `Opening time exception`)}
        loading={loading}
      >

        <HorizontalFieldValue
          label={tr(`NewExceptionSidebar.exceptionDate`, `Exception date or interval`)}
          value={
            <div className="flex flex-col gap-2">
              {formik.values.dateRanges.map((range, index) => {
                return (
                  <div key={index} className="flex flex-row gap-2 items-center">
                    <Calendar
                      locale={i18n.language}
                      dateFormat={shortDateFormat}
                      selectionMode={'range'}
                      showIcon={true}
                      style={{ width: '15rem', height: '38px' }}
                      value={range}
                      name={`dateRanges[${index}]`}
                      onChange={formik.handleChange}
                    />
                    <RingilButton
                      mode={'table'}
                      icon={<SvgDelete />}
                      onClick={() => deleteDate(index)}
                    />
                  </div>
                );
              })}
              {formik.errors?.dateRanges && (
                <div className="mt-2 text-complementary-error">
                  {formik.errors?.dateRanges?.toString()}
                </div>
              )}
            </div>
          }
        />
        <div>
          <RingilButton
            mode={'outlined-rectangle'}
            label={tr(`NewExceptionSidebar.addDateOrInterval`, `Add date or interval`)}
            icon={<SvgPlus />}
            onClick={() => addDate()}
          />
        </div>

        <HorizontalFieldValue
          label={tr(`NewExceptionSidebar.name`, `Name`)}
          value={
            <>
              <InputText style={{ width: '15rem' }} name={'exceptionName'} value={formik.values.exceptionName}
                         onChange={formik.handleChange} />
              <div className="mt-2 text-complementary-error">
                {formik.errors.exceptionName}
              </div>
            </>
          }
        />

        <HorizontalFieldValue
          label={tr(`NewExceptionSidebar.locations`, `Locations`)}
          value={
            <Dropdown
              style={{ width: '15rem' }}
              options={[
                {
                  code: LocationSelectionMode.ALL_LOCATIONS,
                  label: tr('NewExceptionSidebar.allLocationsAllRamps', 'All locations, All ramps'),
                },
                {
                  code: LocationSelectionMode.SELECTED_LOCATIONS,
                  label: tr('NewExceptionSidebar.selectedLocations', 'Selected locations'),
                },
              ]}
              optionLabel={'label'}
              optionValue={'code'}
              name={'locationSelectMode'}
              onChange={formik.handleChange}
              value={formik.values.locationSelectMode}
            />
          }
        />
        {formik.values.locationsAndRamps.length > 0 && (
          <>
            <div className="grid grid-cols-2 mt-2">
              <div>{tr(`NewExceptionSidebar.location`, `Location`)}</div>
              <div>{tr(`NewExceptionSidebar.ramp`, `Ramp`)}</div>
            </div>
            <div>
              {formik.values.locationsAndRamps.map((location, locIndex) => (
                <div
                  key={locIndex}
                  className="grid grid-cols-2 items-center gap-2 py-2 border-b border-gray-100"
                  style={{ alignItems: 'start' }}
                >
                  <InputCheckbox
                    label={location.locationName}
                    value={formik.values.locationsAndRamps[locIndex].enabled}
                    note={formatLocation(location)}
                    onChange={(_, v) => formik.setFieldValue(`locationsAndRamps[${locIndex}].enabled`, v)}
                  />
                  <MultiSelect
                    options={locations[locIndex].ramps}
                    optionLabel={'name'}
                    optionValue={'rampId'}
                    value={formik.values.locationsAndRamps[locIndex].ramps}
                    onChange={formik.handleChange}
                    name={`locationsAndRamps[${locIndex}].ramps`}
                    placeholder={tr('NewExceptionSidebar.allRamps', 'All ramps')}
                    disabled={!formik.values.locationsAndRamps[locIndex].enabled}
                  />

                </div>
              ))}
            </div>
          </>
        )}
        <HorizontalFieldValue
          label={tr(`NewExceptionSidebar.whenWillBeOpen`, `When will be open`)}
          value={
            <RadioButtons
              options={[
                {
                  label: tr(`NewExceptionSidebar.closedWholeDay`, `Closed whole day`),
                  code: OpenHoursExceptionType.CLOSED,
                },
                {
                  label: tr('NewExceptionSidebar.opened', 'Opened'),
                  code: OpenHoursExceptionType.OPEN,
                },
              ]}
              optionLabel={'label'}
              optionValue={'code'}
              onChange={d => {
                formik.setFieldValue('openHourExceptionType', d);
              }}
              value={formik.values.openHourExceptionType}
            />
          }
        />
        {formik.values.openHourExceptionType === OpenHourExceptionTypeEnum.OPEN && (
          <>
            <div className="grid grid-cols-[1.5fr_1.5fr_0.5fr] gap-2 mt-2">
              <div>{tr(`NewExceptionSidebar.openedSince`, `Opened since`)}</div>
              <div>{tr(`NewExceptionSidebar.till`, `Till`)}</div>
            </div>
            {formik.values.openingHours.map((openingHour, index) => (
              <div key={index} className="grid grid-cols-[1.5fr_1.5fr_0.5fr] items-start gap-2 mt-1">
                <div>
                  <InputHourManual
                    value={formik.values.openingHours[index].since}
                    onChange={v => formik.setFieldValue(`openingHours[${index}].since`, v)}
                  />
                  <div className="mt-2 text-complementary-error">
                    {formik.errors.openingHours &&
                      typeof formik.errors.openingHours !== 'string' &&
                      formik.errors.openingHours[index] &&
                      // @ts-ignore
                      formik.errors.openingHours[index].since}
                  </div>
                </div>
                <div>
                  <InputHourManual
                    value={formik.values.openingHours[index].till}
                    onChange={v => formik.setFieldValue(`openingHours[${index}].till`, v)}
                  />
                  <div className="mt-2 text-complementary-error">
                    {formik.errors.openingHours &&
                      typeof formik.errors.openingHours !== 'string' &&
                      formik.errors.openingHours[index] &&
                      // @ts-ignore
                      formik.errors.openingHours[index].till}
                  </div>
                </div>
                <div>
                  <RingilButton
                    mode={'table'}
                    icon={<SvgDelete />}
                    onClick={() => removeOpeningTime(index)}
                  />
                </div>
              </div>
            ))}
            <div className="mt-2">
              <RingilButton
                mode={'outlined-rectangle'}
                label={tr(`NewExceptionSidebar.addInterval`, `Add interval`)}
                icon={<SvgPlus />}
                onClick={() => addOpeningTime()}
              />
            </div>
            <div className="mt-2 text-complementary-error">
              {formik.errors.openingHours && typeof formik.errors.openingHours == 'string' && formik.errors.openingHours}
            </div>
          </>
        )}
        <div className="mt-2">
          <InputCheckbox
            label={tr(`NewExceptionSidebar.applyOnlyForCounterParty`, `Aplikovat jen na partnery`)}
            value={formik.values.isOnlyForExternalPartners}
            onChange={(_, v) => formik.setFieldValue('isOnlyForExternalPartners', v)}
          />
        </div>
      </RingilSidebar>
  );
};

export default NewExceptionSidebar;
